import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ApiCall from "../../../Apicall/ApiCall";
import PublicPage from "../../PublicPage";
import TransactionHeader from "./TransactionHeader";
import DealCompletionStage from "./ownership/DealCompletionStage";
import { DEAL_HOME_PATH } from "../../lib/dealSession";

// The seller's closing page on a page of its own — where the seller lands once they have stated
// that the change of ownership is reported.
//
// Built like DealOwnershipPage, the stage before it: the same shell and header. Unlike the stages
// before it, this is NOT part of the deal flow (lib/dealSession): the deal is finished, so there
// is nothing to leave, no "Leave the deal?" question to ask and no deal group to stay in.
export default function DealCompletionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: id },
        });
        const found = res?.data?.result || res?.result || null;
        if (!cancelled) {
          setDeal(found);
          setLoading(false);
        }
      } catch (_) {
        if (!cancelled) {
          setDeal(null);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Only a signed, uncancelled deal can have been completed at all.
  useEffect(() => {
    if (loading) return;
    if (!deal || !deal.dealComplete || deal.dealCancel) navigate(DEAL_HOME_PATH, { replace: true });
  }, [loading, deal, navigate]);

  const advance = useCallback((to) => navigate(to, { replace: true }), [navigate]);
  const go = useCallback((to) => navigate(to), [navigate]);

  return (
    <PublicPage>
      <div className="flex min-h-screen flex-col bg-ds-canvas">
        <TransactionHeader />
        <main className="ds-scope w-full flex-1 px-4 pb-16 pt-6 md:px-6">
          <div className="ds-form mx-auto w-full">
            {loading || !deal ? (
              <div className="mx-auto w-full" style={{ maxWidth: "42rem" }} role="status" aria-busy="true">
                <span className="sr-only">{t("completion.preparing")}</span>
                <div className="border-t-2 border-ds-ink pt-4" aria-hidden="true">
                  <div className="h-6 w-28 animate-pulse rounded bg-[rgba(20,36,46,.08)]" />
                </div>
                <div className="mt-8 h-7 w-2/3 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
                <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
              </div>
            ) : (
              <DealCompletionStage contractId={id} deal={deal} onAdvance={advance} onNavigate={go} />
            )}
          </div>
        </main>
      </div>
    </PublicPage>
  );
}
